import { useEffect, useMemo } from 'react';
import {
  QueryHookOptions,
  skipToken,
  SuspenseQueryHookOptions,
  useQuery,
  useSuspenseQuery,
} from '@apollo/client';
import {
  useQuery as useRestQuery,
  useSuspenseQuery as useRestSuspenseQuery,
} from '@tanstack/react-query';

import { Job, JobV2 } from '../../../common/interfaces/entities';
import { useAxiosWithAPI } from '../useAxiosWithAPI';
import useIsCustomPlainSQEnabled from '../useIsCustomPlainSQEnabled';
import useIsKeywordMatchingEnabled from '../useIsKeywordMatchingEnabled';
import { getJobByIdQuery } from './jobQueries';
import { normalizeJobV2Response } from './jobV2';

interface UseJobParams
  extends Omit<QueryHookOptions<JobData, JobVariables>, 'variables'> {
  jobId: string;
  useV2?: boolean;
}

interface UseJobSuspenseParams
  extends Omit<SuspenseQueryHookOptions<JobData, JobVariables>, 'variables'> {
  jobId: string;
  skip?: boolean;
  useV2?: boolean;
}

interface JobData {
  getJobById: Job;
}

interface JobVariables {
  jobId: string;
  isCustomPlainSQEnabled: boolean;
}

const transformJobData = (data?: JobData['getJobById']) =>
  data && {
    ...data,
    CreatorId: data.creatorId,
  };

export const fetchJobByIdV2 = async (
  axios: ReturnType<typeof useAxiosWithAPI>,
  jobId: string | undefined
): Promise<Job | null> => {
  if (!jobId) return null;

  const params = {
    include: 'JobSalaries, CompanyAddress, screeningQuestion',
    includeBenefits: 'true',
  };
  const qs = new URLSearchParams(params).toString();
  const response = await axios.get<{ data: JobV2 }>(`/v2/jobs/${jobId}?${qs}`);
  const normalized = normalizeJobV2Response(response.data.data);
  return {
    ...normalized,
    CreatorId: normalized.CreatorId ?? normalized.creatorId,
  };
};

export const useJobSuspense = ({
  jobId,
  skip,
  useV2 = false,
  ...options
}: UseJobSuspenseParams) => {
  const isCustomPlainSQEnabled = useIsCustomPlainSQEnabled();
  const isKeywordMatchingEnabled = useIsKeywordMatchingEnabled();
  const isJobApiV2Enabled = useV2 || isKeywordMatchingEnabled;

  const jobIdV1 = !isJobApiV2Enabled && jobId && !skip ? jobId : undefined;
  const jobIdV2 = isJobApiV2Enabled && jobId && !skip ? jobId : undefined;

  const { data: jobV1, error: v1Error } = useSuspenseQuery<
    JobData,
    JobVariables
  >(
    getJobByIdQuery,
    jobIdV1
      ? {
          variables: {
            jobId: jobIdV1,
            isCustomPlainSQEnabled,
          },
          fetchPolicy: options.fetchPolicy || 'network-only',
          ...options,
        }
      : skipToken
  );

  const axios = useAxiosWithAPI();
  const { data: jobV2 } = useRestSuspenseQuery({
    queryKey: ['job-by-id', jobIdV2],
    queryFn: () => fetchJobByIdV2(axios, jobIdV2),
    gcTime: 0,
  });

  const transformedJobData: Job | undefined = useMemo(() => {
    if (isJobApiV2Enabled) {
      return jobV2 as Job | undefined;
    }
    return transformJobData(jobV1?.getJobById);
  }, [isJobApiV2Enabled, jobV1?.getJobById, jobV2]);

  return {
    job: transformedJobData,
    // NOTE: In TanStack Query Suspense mode, the error field is undefined (errors are thrown to ErrorBoundary).
    // Only return Apollo‑side errors here to maintain backward compatibility with the original interface.
    error: isJobApiV2Enabled ? undefined : v1Error,
  };
};

const useJob = ({ jobId, useV2 = false, ...options }: UseJobParams) => {
  const isCustomPlainSQEnabled = useIsCustomPlainSQEnabled();
  const isKeywordMatchingEnabled = useIsKeywordMatchingEnabled();
  const isJobApiV2Enabled = useV2 || isKeywordMatchingEnabled;

  const jobIdV1 =
    !isJobApiV2Enabled && jobId && !options.skip ? jobId : undefined;
  const jobIdV2 =
    isJobApiV2Enabled && jobId && !options.skip ? jobId : undefined;

  const {
    data: v1Data,
    loading: v1Loading,
    error: v1Error,
  } = useQuery<JobData, JobVariables>(getJobByIdQuery, {
    variables: {
      jobId: jobIdV1 ?? '',
      isCustomPlainSQEnabled,
    },
    skip: !jobIdV1,
    fetchPolicy: options.fetchPolicy || 'network-only',
    ...options,
  });

  const axios = useAxiosWithAPI();
  const {
    data: jobV2,
    isFetching: v2Fetching,
    error: v2Error,
  } = useRestQuery({
    queryKey: ['job-by-id', jobIdV2],
    queryFn: () => fetchJobByIdV2(axios, jobIdV2),
    gcTime: 0,
    enabled: Boolean(jobIdV2) && options.fetchPolicy !== 'cache-only',
  });

  // handle options.onCompleted, to make compatiable with V1
  useEffect(() => {
    if (isJobApiV2Enabled && jobV2 && options.onCompleted) {
      options.onCompleted({ getJobById: jobV2 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJobApiV2Enabled, jobV2]);

  const loading = isJobApiV2Enabled ? v2Fetching : v1Loading;
  const error = isJobApiV2Enabled ? (v2Error as Error | undefined) : v1Error;

  const transformedJobData: Job | undefined = useMemo(() => {
    if (isJobApiV2Enabled) {
      return jobV2 as Job | undefined;
    }
    return transformJobData(v1Data?.getJobById);
  }, [isJobApiV2Enabled, v1Data?.getJobById, jobV2]);

  return {
    job: transformedJobData,
    loading,
    error,
  };
};

export default useJob;
